import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import {
  timeToActionColour,
  completeActionColour,
  noMoreActionsColour,
  getStatusColorByRole,
} from "@/components/widgets/tradeActionSummary/helper/statusCellStylingHelper";

// ─── Types ───────────────────────────────────────────────────────────────────

export type NotificationChannel = "sound" | "bell" | "browser";

export type NotificationGroupKey =
  | "actionRequired"
  | "waitingCounterparty"
  | "noFurtherAction"
  | "tradeSettingsNegotiation";

export interface StatusNotificationToggles {
  sound: boolean;
  bell: boolean;
  browser: boolean; // controls browser popup notification (silent: true suppresses Chrome sound)
}

// ─── Negotiation Events (LQC-637 / LQC-1745) ─────────────────────────────────
// The RP/OP "Request Information" negotiation produces events that are NOT
// ExecutionStrategyStatus values — they are the requestInformationStore
// transitions (Requested → True / Rejected). The string values double as the
// row labels rendered in the negotiation notification column.
export enum NotificationNegotiationEvent {
  RequestReceived = "Request Received", // OP: an RP has requested a field
  RequestAccepted = "Request Accepted", // RP: OP released the requested field
  RequestRejected = "Request Rejected", // RP: OP declined the request
}

/** All valid notification status keys — ES statuses plus negotiation events. */
export type NotificationStatusKey =
  | ExecutionStrategyStatus
  | NotificationNegotiationEvent;

export interface NotificationGroupDef {
  label: string;
  description: string;
  statuses: NotificationStatusKey[];
  defaultSound: string;
}

// ─── Sound Options ───────────────────────────────────────────────────────────

export const SOUND_OPTIONS = [
  { value: "none", label: "No Sound", file: "" },
  { value: "notification1", label: "Cube", file: "notification1.wav" },
  // v1: hidden — { value: "notification2", label: "Chime", file: "notification2.wav" },
  // v1: hidden — { value: "notification3", label: "Bell", file: "notification3.wav" },
  { value: "notification4", label: "Success", file: "notification4.wav" },
  // v1: hidden — { value: "notification5", label: "Confirmation", file: "notification5.wav" },
  { value: "notification6", label: "Rolling Dice", file: "notification6.wav" },
  { value: "notification7", label: "Kangaroo", file: "notification7.wav" },
];

// ─── Group Definitions ───────────────────────────────────────────────────────
// Groups based on the OP status color coding from statusCellStylingHelper.tsx:
//   Orange (#EA8A48) = Action Required by current user
//   Waiting          = Counterparty needs to act
//   Gray             = Terminal / No further action

export const NOTIFICATION_GROUPS: Record<
  NotificationGroupKey,
  NotificationGroupDef
> = {
  actionRequired: {
    label: "Buy Side",
    description: "Statuses requiring action from you",
    statuses: [
      ExecutionStrategyStatus.New,
      ExecutionStrategyStatus.Sent,
      ExecutionStrategyStatus.Acknowledged,
      ExecutionStrategyStatus.Working,
      ExecutionStrategyStatus.Quoted,
      ExecutionStrategyStatus.OffQuote,
      ExecutionStrategyStatus.Executed,
      ExecutionStrategyStatus.Filled,
      ExecutionStrategyStatus.NoMoreFills,
      //
      ExecutionStrategyStatus.ChangePending,
      ExecutionStrategyStatus.Rejected,
    ],
    defaultSound: "notification1",
  },
  waitingCounterparty: {
    label: "Sell Side",
    description: "Statuses waiting on counterparty response",
    statuses: [
      ExecutionStrategyStatus.Received,
      ExecutionStrategyStatus.ReSent,
      ExecutionStrategyStatus.Awarded,
      //
      ExecutionStrategyStatus.Recalled,
      ExecutionStrategyStatus.ReQuoteRequested,
      ExecutionStrategyStatus.ExecutedOPChangeRequested,
      // ExecutionStrategyStatus.QuoteExpired, - Removed
    ],
    defaultSound: "notification1",
  },
  noFurtherAction: {
    label: "Other",
    description: "Terminal statuses with no further action needed",
    statuses: [
      ExecutionStrategyStatus.Affirmed,
      ExecutionStrategyStatus.Cancelled,
      ExecutionStrategyStatus.DealtAway,
      ExecutionStrategyStatus.NotDealt,
      ExecutionStrategyStatus.ReCreated,
    ],
    defaultSound: "notification1",
  },
  // LQC-637 / LQC-1745 — RP/OP Request Information negotiation events. Gated in
  // the UI behind the rpRequestInformationButtons feature flag.
  tradeSettingsNegotiation: {
    label: "Negotiation",
    description: "RP/OP request information negotiation events",
    statuses: [
      NotificationNegotiationEvent.RequestReceived,
      NotificationNegotiationEvent.RequestAccepted,
      NotificationNegotiationEvent.RequestRejected,
    ],
    defaultSound: "notification1",
  },
};

// Ordered group keys for consistent UI rendering
export const NOTIFICATION_GROUP_ORDER: NotificationGroupKey[] = [
  "actionRequired",
  "waitingCounterparty",
  "noFurtherAction",
  "tradeSettingsNegotiation",
];

// ─── Helper Functions ────────────────────────────────────────────────────────

/**
 * Resolves the preference key for a status.
 * All statuses now map directly to their enum value.
 */
export function getPreferenceKey(
  status: ExecutionStrategyStatus,
  _role?: string,
): string {
  return status;
}

/**
 * Find which group a status belongs to.
 */
export function getGroupForStatus(status: string): NotificationGroupKey | null {
  for (const [key, group] of Object.entries(NOTIFICATION_GROUPS)) {
    if ((group.statuses as string[]).includes(status)) {
      return key as NotificationGroupKey;
    }
  }
  return null;
}

/**
 * Get the sound file value for a status via its group's sound selection.
 */
export function getSoundForStatus(
  status: string,
  groupSounds: Record<NotificationGroupKey, string>,
): string {
  const groupKey = getGroupForStatus(status);
  if (!groupKey) return "none";
  const selectedSound = groupSounds[groupKey] || "none";
  // Fallback: if selected sound was removed from SOUND_OPTIONS, use group default
  if (
    selectedSound !== "none" &&
    !SOUND_OPTIONS.some((o) => o.value === selectedSound)
  ) {
    return NOTIFICATION_GROUPS[groupKey].defaultSound;
  }
  return selectedSound;
}

/**
 * Get the sound file path (e.g., "notification1.wav") from a sound setting value.
 */
export function getSoundFilePath(soundValue: string): string {
  if (!soundValue || soundValue === "none") return "";
  const option = SOUND_OPTIONS.find((o) => o.value === soundValue);
  if (option) return option.file;
  // Fallback: saved preference references a removed sound — use first available
  const fallback = SOUND_OPTIONS.find((o) => o.value !== "none");
  return fallback?.file || "";
}

/**
 * Build the default status preferences map.
 * Sound and bell default OFF; browser defaults ON for all statuses.
 * Browser controls native popup notification (silent: true suppresses Chrome sound).
 */
export function buildDefaultPreferences(): Record<
  string,
  StatusNotificationToggles
> {
  const preferences: Record<string, StatusNotificationToggles> = {};

  for (const group of Object.values(NOTIFICATION_GROUPS)) {
    for (const status of group.statuses) {
      preferences[status] = {
        sound: false,
        bell: false,
        browser: true,
      };
    }
  }

  return preferences;
}

/**
 * Build the default group sounds map.
 */
export function buildDefaultGroupSounds(): Record<
  NotificationGroupKey,
  string
> {
  const groupSounds: Record<string, string> = {};
  for (const [key, group] of Object.entries(NOTIFICATION_GROUPS)) {
    groupSounds[key] = group.defaultSound;
  }
  return groupSounds as Record<NotificationGroupKey, string>;
}

/**
 * Colour per group, derived from the status cell renderer's source-of-truth constants.
 * Any future colour changes in statusCellStylingHelper automatically propagate here.
 */
export const GROUP_COLOURS: Record<NotificationGroupKey, string> = {
  actionRequired: timeToActionColour, // "#EA8A48"
  waitingCounterparty: completeActionColour, // "#06AA6D"
  noFurtherAction: noMoreActionsColour, // "gray"
  tradeSettingsNegotiation: timeToActionColour, // orange — matches the request dot in the strip
};

/**
 * Get the notification dot colour for a status (hex or named colour).
 * Used by NotificationCenter for inline backgroundColor styling.
 */
export function getStatusColor(status: string, role?: string): string {
  // When role is provided, use the same arrays as the AG Grid status cell renderer
  if (role) {
    return getStatusColorByRole(status, role);
  }
  // Fallback: group-based lookup (for cases without role)
  const group = getGroupForStatus(status);
  if (group) return GROUP_COLOURS[group];
  return "gray";
}
